import React, { useMemo, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { Layout } from 'features/common/components/Layout';
import {
	Box,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Typography,
} from '@mui/material';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from 'store';
import { isAdminOrSuperAdmin, isOperatorOrHigher } from '../../features/auth/helpers';
import { useCurrentUserTeam } from 'features/auth/hooks/useCurrentUserTeam';
import { CountryCodeEnum, RolesEnum } from '../../features/common/types';
import { HomeCard, HomeTeamView } from './types';
import { ADMIN_TEAM, EMISIONES_TEAM, readStoredHomeTeamView, persistHomeTeamView } from './constants';
import { administracionCardData } from './administracionCardData';
import { emisionesCardData } from './emisionesCardData';
import { HomeCardGrid } from './HomeCardGrid';
import routes from 'constants/routes';

// Teams que tienen dashboards de ventas — el resto sigue viendo el Home normal
const SALES_TEAMS = ['WTC UY', 'PY', 'CENTRO UY', 'BO', 'CO'];

const defaultCardData: HomeCard[] = [
	{
		id: 1,
		icon: '📝',
		title: 'Crear coti',
		description: 'Crear una nueva cotización a partir de un deal',
		link: '/quotations/new',
		isExternal: false,
		allowedCountries: [
			CountryCodeEnum.URUGUAY,
			CountryCodeEnum.PARAGUAY,
			CountryCodeEnum.BOLIVIA,
			CountryCodeEnum.COLOMBIA,
			CountryCodeEnum.TN_ITAU_PY,
		],
		visibleTo: 'all',
	},
	{
		id: 2,
		icon: '🛒',
		title: 'Crear orden',
		description: 'Crear una nueva orden de venta a partir de un deal',
		link: '/salesorder/new',
		isExternal: false,
		allowedCountries: [
			CountryCodeEnum.URUGUAY,
			CountryCodeEnum.PARAGUAY,
			CountryCodeEnum.BOLIVIA,
			CountryCodeEnum.COLOMBIA,
			CountryCodeEnum.TN_ITAU_PY,
		],
		visibleTo: 'all',
	},
	{
		id: 3,
		icon: '🏝️',
		title: 'Paquetes',
		description: 'Ver paquetes disponibles y crear una coti a partir del mismo',
		link: '/stats/packages',
		isExternal: false,
		allowedCountries: [
			CountryCodeEnum.URUGUAY,
			CountryCodeEnum.PARAGUAY,
			CountryCodeEnum.BOLIVIA,
			CountryCodeEnum.COLOMBIA,
			CountryCodeEnum.TN_ITAU_PY,
		],
		visibleTo: 'all',
	},
	{
		id: 4,
		icon: '💳',
		title: 'Cobrar c/tarjeta',
		description: (
			<>
				Enviar un link de pago de <strong>aerolinea</strong>.
			</>
		),
		link: '/payments/order/1',
		isExternal: false,
		allowedCountries: [CountryCodeEnum.URUGUAY],
		visibleTo: 'all',
	},
	{
		id: 5,
		icon: '💳',
		title: 'Agregar tarjeta a la cartera',
		description: 'Cargá la tarjeta en forma interna (emisiones), sin link al cliente.',
		link: '/operations/wallet/add-card',
		isExternal: false,
		allowedCountries: [CountryCodeEnum.URUGUAY],
		visibleTo: 'operator',
	},
	{
		id: 6,
		icon: '💳',
		title: 'Cobrar c/tarjeta',
		description: (
			<>
				Enviar un link de pago de <strong>pasarela</strong>.
			</>
		),
		link: '/payments/order/2',
		isExternal: false,
		allowedCountries: [CountryCodeEnum.URUGUAY],
		visibleTo: 'all',
	},
	{
		id: 7,
		icon: '💳',
		title: 'Cobrar c/tarjeta',
		description: 'Enviar un link de pago de pasarela.',
		link: '/payments/order/3',
		isExternal: false,
		allowedCountries: [CountryCodeEnum.PARAGUAY],
		visibleTo: 'all',
	},
	{
		id: 8,
		icon: '💳',
		title: 'Cobrar c/tarjeta',
		description: 'Enviar un link de pago de pasarela.',
		link: '/payments/order/5',
		isExternal: false,
		allowedCountries: [CountryCodeEnum.COLOMBIA],
		visibleTo: 'all',
	},
	{
		id: 9,
		icon: '💳',
		title: 'Cobrar c/tarjeta',
		description: 'Enviar un link de pago de pasarela.',
		link: '/payments/order/4',
		isExternal: false,
		allowedCountries: [CountryCodeEnum.BOLIVIA],
		visibleTo: 'all',
	},
	{
		id: 10,
		icon: '💳',
		title: 'Cobrar c/tarjeta',
		description: 'Enviar un link de pago de pasarela.',
		link: '/payments/viaje-naranja-order',
		isExternal: false,
		allowedCountries: [CountryCodeEnum.TN_ITAU_PY],
		visibleTo: 'all',
	},
	{
		id: 11,
		icon: '💵',
		title: 'Cobrar c/efectivo',
		description: 'Registrar el cobro en efectivo.',
		link: '/payments/order-cash',
		isExternal: false,
		allowedCountries: [
			CountryCodeEnum.URUGUAY,
			CountryCodeEnum.PARAGUAY,
			CountryCodeEnum.BOLIVIA,
			CountryCodeEnum.COLOMBIA,
		],
		visibleTo: 'all',
	},
	{
		id: 12,
		icon: '🏦',
		title: 'Cobrar c/trf',
		description: 'Validar una transferencia bancaria.',
		link: '/payments/wiretransfers/check',
		isExternal: false,
		allowedCountries: [
			CountryCodeEnum.URUGUAY,
			CountryCodeEnum.PARAGUAY,
			CountryCodeEnum.BOLIVIA,
			CountryCodeEnum.COLOMBIA,
			CountryCodeEnum.TN_ITAU_PY,
		],
		visibleTo: 'all',
	},
	{
		id: 15,
		icon: '💰',
		title: 'Cuentas bancarias',
		description: 'Información de las cuentas bancarias habilitadas para realizar cobros',
		link: 'https://cuentas.mevuelo.com/uy',
		isExternal: true,
		allowedCountries: [CountryCodeEnum.URUGUAY],
		visibleTo: 'all',
	},
	{
		id: 16,
		icon: '💰',
		title: 'Cuentas bancarias',
		description: 'Información de las cuentas bancarias habilitadas para realizar cobros',
		link: 'https://cuentas.mevuelo.com/py',
		isExternal: true,
		allowedCountries: [CountryCodeEnum.PARAGUAY],
		visibleTo: 'all',
	},
	{
		id: 17,
		icon: '💰',
		title: 'Cuentas bancarias',
		description: 'Información de las cuentas bancarias habilitadas para realizar cobros',
		link: 'https://cuentas.mevuelo.com/bo',
		isExternal: true,
		allowedCountries: [CountryCodeEnum.BOLIVIA],
		visibleTo: 'all',
	},
	{
		id: 18,
		icon: '💰',
		title: 'Cuentas bancarias',
		description: 'Información de las cuentas bancarias habilitadas para realizar cobros',
		link: 'https://cuentas.mevuelo.com/co',
		isExternal: true,
		allowedCountries: [CountryCodeEnum.COLOMBIA],
		visibleTo: 'all',
	},
	// {
	// 	id: 19,
	// 	icon: '↩️',
	// 	title: 'Devoluciones',
	// 	description: 'Solicitar una devolucion para un cliente',
	// 	link: '/devoluciones',
	// 	isExternal: false,
	// 	allowedCountries: [
	// 		CountryCodeEnum.URUGUAY,
	// 		CountryCodeEnum.PARAGUAY,
	// 		CountryCodeEnum.BOLIVIA,
	// 		CountryCodeEnum.COLOMBIA,
	// 		CountryCodeEnum.TN_ITAU_PY,
	// 	],
	// 	visibleTo: 'all',
	// },
	// {
	// 	id: 20,
	// 	icon: '📊',
	// 	title: 'Reporting',
	// 	description: 'Acceder al reporte de Looker Studio para ver el estado de mis ventas',
	// 	link: '/report',
	// 	isExternal: false,
	// 	allowedCountries: [
	// 		CountryCodeEnum.URUGUAY,
	// 		CountryCodeEnum.PARAGUAY,
	// 		CountryCodeEnum.BOLIVIA,
	// 		CountryCodeEnum.COLOMBIA,
	// 		CountryCodeEnum.TN_ITAU_PY,
	// 	],
	// 	visibleTo: 'all',
	// },
	{
		id: 21,
		icon: '💳',
		title: 'Pago de servicio',
		description: 'Conciliación de pago directo con tarjeta de crédito corporativa',
		link: 'https://forms.gle/JSpyosUzdbv1ySfB6',
		isExternal: true,
		allowedCountries: [
			CountryCodeEnum.URUGUAY,
			CountryCodeEnum.PARAGUAY,
			CountryCodeEnum.BOLIVIA,
			CountryCodeEnum.COLOMBIA,
			CountryCodeEnum.TN_ITAU_PY,
		],
		visibleTo: 'operator',
	},
	{
		id: 22,
		icon: '🤝🏼',
		title: 'Nimbux CRM',
		description: 'CRM y sistema de tickets',
		link: 'https://crm.mevuelo.com/',
		isExternal: true,
		allowedCountries: [
			CountryCodeEnum.URUGUAY,
			CountryCodeEnum.PARAGUAY,
			CountryCodeEnum.BOLIVIA,
			CountryCodeEnum.COLOMBIA,
			CountryCodeEnum.TN_ITAU_PY,
		],
		visibleTo: 'operator',
	},
	{
		id: 30,
		icon: '✈️',
		title: 'Rutas Aéreas',
		description: 'Precios de vuelos por ruta — cuáles bajaron y cuáles subieron',
		link: routes.airRoutes.root,
		isExternal: false,
		allowedCountries: [
			CountryCodeEnum.URUGUAY,
			CountryCodeEnum.PARAGUAY,
			CountryCodeEnum.BOLIVIA,
			CountryCodeEnum.COLOMBIA,
			CountryCodeEnum.TN_ITAU_PY,
		],
		visibleTo: 'operator',
	},
	// {
	// 	id: 23,
	// 	icon: '📖',
	// 	title: 'Wiki',
	// 	description: 'Nuestro centro de conocimiento',
	// 	link: 'https://wiki.mevuelo.com/',
	// 	isExternal: true,
	// 	allowedCountries: [
	// 		CountryCodeEnum.URUGUAY,
	// 		CountryCodeEnum.PARAGUAY,
	// 		CountryCodeEnum.BOLIVIA,
	// 		CountryCodeEnum.COLOMBIA,
	// 		CountryCodeEnum.TN_ITAU_PY,
	// 	],
	// 	visibleTo: 'all',
	// },
];

function filterDefaultCards(cards: HomeCard[], country: CountryCodeEnum, user: RootState['auth']['user']): HomeCard[] {
	return cards.filter(
		(card) =>
			card.allowedCountries?.includes(country) &&
			(card.visibleTo === 'all' ||
				(card.visibleTo === 'operator' && isOperatorOrHigher(user)) ||
				(card.visibleTo === 'admin' && isAdminOrSuperAdmin(user))),
	);
}

// ─── Home ─────────────────────────────────────────────────────────────────────

const Home = (): JSX.Element => {
	const auth = useSelector((state: RootState) => state.auth, shallowEqual);
	const { team, loading: teamLoading } = useCurrentUserTeam();
	const canToggleView = isAdminOrSuperAdmin(auth.user);
	const [adminView, setAdminView] = useState<HomeTeamView>(readStoredHomeTeamView);

	const activeView: HomeTeamView = useMemo(() => {
		if (canToggleView) {
			return adminView;
		}
		if (team === EMISIONES_TEAM) {
			return 'EMISIONES';
		}
		if (team === ADMIN_TEAM) {
			return 'ADMIN';
		}
		return 'DEFAULT';
	}, [canToggleView, adminView, team]);

	const filteredCards = useMemo(() => {
		if (activeView === 'EMISIONES') {
			return emisionesCardData;
		}
		if (activeView === 'ADMIN') {
			return administracionCardData;
		}
		return filterDefaultCards(defaultCardData, auth.country, auth.user);
	}, [activeView, auth.country, auth.user]);

	const handleViewChange = (event: SelectChangeEvent<HomeTeamView>) => {
		const value = event.target.value as HomeTeamView;
		setAdminView(value);
		persistHomeTeamView(value);
	};

	// Redirect para teams de ventas — todos los hooks ya fueron llamados antes de este punto.
	// Un superadmin SIN equipo de ventas NO se redirige: se queda en la Home normal y entra
	// a los dashboards por los quick links (su dashboard personal estaría vacío).
	if (!teamLoading && team && SALES_TEAMS.includes(team)) {
		if (isAdminOrSuperAdmin(auth.user)) {
			// Admin/SuperAdmin con equipo de ventas → dashboard del equipo
			return <Redirect to={routes.salesDashboard.team(team)} />;
		}
		if (auth.user?.role === RolesEnum.SELLER && auth.user.name) {
			// Seller → su propio dashboard
			return <Redirect to={routes.salesDashboard.seller(auth.user.name)} />;
		}
	}

	if (teamLoading) {
		return (
			<Layout>
				<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
					<CircularProgress />
				</Box>
			</Layout>
		);
	}

	return (
		<Layout>
			<Box sx={{ flexGrow: 1, padding: 2, margin: '0 20%' }}>
				{canToggleView ? (
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
						<FormControl size="small" sx={{ minWidth: 220 }}>
							<InputLabel id="home-team-view-label">Vista de equipo</InputLabel>
							<Select
								labelId="home-team-view-label"
								value={adminView}
								label="Vista de equipo"
								onChange={handleViewChange}
							>
								<MenuItem value="DEFAULT">Default</MenuItem>
								<MenuItem value="EMISIONES">Emisiones</MenuItem>
								<MenuItem value="ADMIN">Administración</MenuItem>
							</Select>
						</FormControl>
					</Box>
				) : null}

				{activeView === 'EMISIONES' ? (
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
						Escritorio del equipo de Emisiones
					</Typography>
				) : null}

				{activeView === 'ADMIN' ? (
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
						Escritorio del equipo de Administración
					</Typography>
				) : null}

				<HomeCardGrid cards={filteredCards} country={auth.country} viewKey={activeView} />
			</Box>
		</Layout>
	);
};

export default Home;
